import React, { useState, useCallback, useEffect } from 'react';
import { callApi } from '../../api/call';
import { useCompany } from '../../CompanyContext';
import { useNotification } from '../../context/NotificationContext';
import RescheduleDialog from './RescheduleDialog';
import {
  canSendCheckInLink,
  isAwaitingClientConfirmation,
  isCheckedIn,
} from '../../utils/appointmentWorkflow';
import { isPendingConfirmationReplyOnlyEnabled } from '../../config/featureFlags';
import './styles/QuickActions.css';

const MANUAL_REMINDER_COOLDOWN_MS = 10 * 60 * 1000;
const CONFIRMATION_LINK_COOLDOWN_MS = 10 * 60 * 1000;
const CHECK_IN_LINK_COOLDOWN_MS = 10 * 60 * 1000;
const QUICK_ACTION_TIMEOUT_MS = 30000;
const getDateFromValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatCooldown = (msLeft) => {
  const totalSeconds = Math.max(0, Math.ceil(msLeft / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes <= 0) return `${seconds}s`;
  if (seconds === 0) return `${minutes}m`;
  return `${minutes}m ${seconds}s`;
};

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
);
const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
);
const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
);
const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
);
const LinkIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07L10.7 5.24"/><path d="M14 11a5 5 0 0 0-7.07 0L4.1 13.83a5 5 0 0 0 7.07 7.07L13.3 18.76"/></svg>
);
const PersonOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="23" y1="11" x2="17" y2="11"/><path d="M16 8l5-5 5 5"/></svg>
);
const QuickActions = ({
  appointment,
  onConfirm,
  onCancel,
  onReschedule,
  onNoShow,
  companyId
}) => {
  const { companyData } = useCompany();
  const { showToast } = useNotification();
  const appointmentStatus = appointment?.status?.toLowerCase();
  const bookingSettings = companyData?.settings?.bookingSettings || {};
  const awaitingClientConfirmation = isAwaitingClientConfirmation(appointment);
  const checkInLinkState = canSendCheckInLink(appointment, bookingSettings);
  const appointmentIsCheckedIn = isCheckedIn(appointment);
  const isConfirmedAppointment = appointmentStatus === 'confirmed' || appointment?.clientConfirmed === true;
  const [modal, setModal] = useState({ open: false, type: null, title: '', message: '' });
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState('');
  const [sendingReminder, setSendingReminder] = useState(false);
  const [sendingConfirmationRequest, setSendingConfirmationRequest] = useState(false);
  const [sendingCheckInLink, setSendingCheckInLink] = useState(false);
  const [reminderChannel, setReminderChannel] = useState('email');
  const [submitting, setSubmitting] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [reminderCooldownUntil, setReminderCooldownUntil] = useState(null);
  const [reminderCooldownNow, setReminderCooldownNow] = useState(Date.now());
  const [confirmationCooldownUntil, setConfirmationCooldownUntil] = useState(null);
  const [confirmationCooldownNow, setConfirmationCooldownNow] = useState(Date.now());
  const [checkInLinkCooldownUntil, setCheckInLinkCooldownUntil] = useState(null);
  const [checkInLinkCooldownNow, setCheckInLinkCooldownNow] = useState(Date.now());
  const clientEmail = (appointment?.clientEmail || appointment?.customerInfo?.email || '').trim();
  const clientPhone = (appointment?.clientPhone || appointment?.customerInfo?.phone || '').trim();
  const hasReminderEmail = Boolean(clientEmail);
  const hasReminderPhone = Boolean(clientPhone);
  const reminderCooldownActive = Boolean(reminderCooldownUntil && reminderCooldownUntil > reminderCooldownNow);
  const reminderCooldownLabel = reminderCooldownActive ? formatCooldown(reminderCooldownUntil - reminderCooldownNow) : '';
  const confirmationCooldownActive = Boolean(confirmationCooldownUntil && confirmationCooldownUntil > confirmationCooldownNow);
  const confirmationCooldownLabel = confirmationCooldownActive ? formatCooldown(confirmationCooldownUntil - confirmationCooldownNow) : '';
  const checkInLinkCooldownActive = Boolean(checkInLinkCooldownUntil && checkInLinkCooldownUntil > checkInLinkCooldownNow);
  const checkInLinkCooldownLabel = checkInLinkCooldownActive ? formatCooldown(checkInLinkCooldownUntil - checkInLinkCooldownNow) : '';
  const getDefaultReminderChannel = useCallback(() => {
    if (hasReminderEmail && hasReminderPhone) return 'both';
    if (hasReminderPhone) return 'sms';
    return 'email';
  }, [hasReminderEmail, hasReminderPhone]);

  const getDialogContent = (type) => {
    const name = appointment?.clientName || 'Client';
    return {
      confirm: { title: 'Confirm Appointment', message: `Confirm the appointment for ${name}?` },
      cancel: { title: 'Cancel Appointment', message: `Cancel the appointment for ${name}?` },
      noShow: { title: 'Mark as No Show', message: `Mark ${name}'s appointment as no show?` },
      sendReminder: {
        title: '',
        message: <>Send a reminder to <strong>{name}</strong>?</>
      },
      sendConfirmationRequest: {
        title: '',
        message: isPendingConfirmationReplyOnlyEnabled() ? (
          <>Send a confirmation SMS to <strong>{name}</strong>? They can reply CONFIRM.</>
        ) : (
          <>Send a confirmation link to <strong>{name}</strong>?</>
        )
      },
      sendCheckInLink: {
        title: '',
        message: <>Send a self check-in link to <strong>{name}</strong>?</>
      },
    }[type] || { title: '', message: '' };
  };

  const isDisabled = (type) => {
    const status = appointmentStatus;
    const now = new Date();
    const appointmentTime = appointment?.dateTime?.toDate ? appointment.dateTime.toDate() : new Date(appointment?.dateTime);

    if (status === 'completed') return true;
    switch (type) {
      case 'confirm':
        return status === 'cancelled' || status === 'confirmed' || status === 'no show' || appointment?.clientConfirmed === true;
      case 'cancel':
        return status === 'cancelled' || status === 'no show';
      case 'noShow':
        return status === 'cancelled' || status === 'no show' || now < appointmentTime;
      case 'reschedule':
        return status === 'cancelled' || status === 'no show';
      case 'sendReminder':
        return (
          !awaitingClientConfirmation ||
          (!hasReminderEmail && !hasReminderPhone) ||
          reminderCooldownActive ||
          sendingReminder ||
          !(companyId || companyData?.id) ||
          !appointment?.id
        );
      case 'sendConfirmationRequest':
        return (
          !awaitingClientConfirmation ||
          !hasReminderPhone ||
          confirmationCooldownActive ||
          sendingConfirmationRequest ||
          !(companyId || companyData?.id) ||
          !appointment?.id
        );
      case 'sendCheckInLink':
        return (
          !checkInLinkState.eligible ||
          appointmentIsCheckedIn ||
          !hasReminderPhone ||
          checkInLinkCooldownActive ||
          sendingCheckInLink ||
          !(companyId || companyData?.id) ||
          !appointment?.id
        );
      default:
        return false;
    }
  };

  const openModal = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    const content = getDialogContent(type);
    setModal({ open: true, type, title: content.title, message: content.message });
    if (type === 'cancel') {
      setCancelReason('');
      setCancelReasonError('');
    }
    if (type === 'sendReminder') {
      setReminderChannel(getDefaultReminderChannel());
    }
  };

  const closeModal = useCallback(() => {
    setModal((m) => ({ ...m, open: false }));
    setCancelReason('');
    setCancelReasonError('');
  }, []);

  const doConfirm = async () => {
    if (modal.type === 'cancel' && !cancelReason.trim()) {
      setCancelReasonError('Cancellation reason is required');
      return;
    }
    if (modal.type === 'sendReminder') {
      doSendReminder();
      return;
    }
    if (modal.type === 'sendConfirmationRequest') {
      doSendConfirmationRequest();
      return;
    }
    if (modal.type === 'sendCheckInLink') {
      doSendCheckInLink();
      return;
    }

    const actions = {
      confirm: () => onConfirm({ ...appointment, updatedBy: 'employee', lastAction: 'confirmed' }),
      cancel: () => onCancel({ ...appointment, cancelReason: cancelReason.trim(), updatedBy: 'employee', lastAction: 'cancelled' }),
      noShow: () => onNoShow({ ...appointment, updatedBy: 'employee', lastAction: 'no show' })
    };

    const action = actions[modal.type];
    if (!action) return;

    try {
      setSubmitting(true);
      await Promise.race([
        Promise.resolve(action()),
        new Promise((_, reject) => {
          setTimeout(() => {
            reject(new Error('Action timed out. Check your connection and try again.'));
          }, QUICK_ACTION_TIMEOUT_MS);
        }),
      ]);
      closeModal();
    } catch (error) {
      console.error('Quick action failed:', error);
      showToast(error?.message || 'Action failed. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const doSendReminder = async () => {
    try {
      setSendingReminder(true);
      const companyKey = companyId || companyData?.id;
      const channels = reminderChannel === 'both' ? ['email', 'sms'] : [reminderChannel];
      const res = await callApi(
        `/api/companies/${encodeURIComponent(companyKey)}/appointments/${encodeURIComponent(appointment?.id)}/reminders`,
        { channels }
      );
      const ok = res?.data?.success !== false;
      if (res?.data?.cooldownUntil) {
        setReminderCooldownUntil(res.data.cooldownUntil);
      }
      showToast(
        ok ? (res?.data?.message || 'Reminder sent') : (res?.data?.message || 'Could not send'),
        ok ? 'success' : 'error'
      );
    } catch (err) {
      showToast('Failed to send reminder', 'error');
    } finally {
      setSendingReminder(false);
    }
    closeModal();
  };

  const doSendCheckInLink = async () => {
    try {
      setSendingCheckInLink(true);
      const companyKey = companyId || companyData?.id;
      const res = await callApi(
        `/api/companies/${encodeURIComponent(companyKey)}/appointments/${encodeURIComponent(appointment?.id)}/check-in-sms`,
        {}
      );
      const ok = res?.data?.success !== false;
      if (res?.data?.cooldownUntil) {
        setCheckInLinkCooldownUntil(res.data.cooldownUntil);
      }
      showToast(
        ok ? (res?.data?.message || 'Check-in link sent') : (res?.data?.message || 'Could not send check-in link'),
        ok ? 'success' : 'error'
      );
    } catch (err) {
      const message = err?.message || 'Failed to send check-in link';
      showToast(message, 'error');
    } finally {
      setSendingCheckInLink(false);
    }
    closeModal();
  };

  const doSendConfirmationRequest = async () => {
    try {
      setSendingConfirmationRequest(true);
      const companyKey = companyId || companyData?.id;
      const res = await callApi(
        `/api/companies/${encodeURIComponent(companyKey)}/appointments/${encodeURIComponent(appointment?.id)}/confirmation-sms`,
        {}
      );
      const ok = res?.data?.success !== false;
      if (res?.data?.cooldownUntil) {
        setConfirmationCooldownUntil(res.data.cooldownUntil);
      }
      showToast(
        ok
          ? (res?.data?.message ||
              (isPendingConfirmationReplyOnlyEnabled()
                ? 'Confirmation SMS sent'
                : 'Confirmation link sent'))
          : (res?.data?.message ||
              (isPendingConfirmationReplyOnlyEnabled()
                ? 'Could not send confirmation SMS'
                : 'Could not send confirmation link')),
        ok ? 'success' : 'error'
      );
    } catch (err) {
      showToast(
        isPendingConfirmationReplyOnlyEnabled()
          ? 'Failed to send confirmation SMS'
          : 'Failed to send confirmation link',
        'error'
      );
    } finally {
      setSendingConfirmationRequest(false);
    }
    closeModal();
  };

  const handleRescheduleClose = useCallback(() => setRescheduleOpen(false), []);
  const handleRescheduleConfirm = useCallback((newDateTime) => {
    onReschedule(appointment, newDateTime);
    setRescheduleOpen(false);
  }, [appointment, onReschedule]);

  useEffect(() => {
    const lastSentAt = getDateFromValue(appointment?.manualReminderLastSentAt);
    if (!lastSentAt) {
      setReminderCooldownUntil(null);
      return;
    }

    setReminderCooldownUntil(lastSentAt.getTime() + MANUAL_REMINDER_COOLDOWN_MS);
  }, [appointment?.id, appointment?.manualReminderLastSentAt]);

  useEffect(() => {
    if (!reminderCooldownActive) return undefined;

    const intervalId = window.setInterval(() => {
      setReminderCooldownNow(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [reminderCooldownActive]);

  useEffect(() => {
    if (!reminderCooldownActive) {
      setReminderCooldownNow(Date.now());
    }
  }, [reminderCooldownActive]);

  useEffect(() => {
    const lastSentAt = getDateFromValue(appointment?.pendingConfirmationLinkSentAt);
    if (!lastSentAt) {
      setConfirmationCooldownUntil(null);
      return;
    }

    setConfirmationCooldownUntil(lastSentAt.getTime() + CONFIRMATION_LINK_COOLDOWN_MS);
  }, [appointment?.id, appointment?.pendingConfirmationLinkSentAt]);

  useEffect(() => {
    if (!confirmationCooldownActive) return undefined;

    const intervalId = window.setInterval(() => {
      setConfirmationCooldownNow(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [confirmationCooldownActive]);

  useEffect(() => {
    if (!confirmationCooldownActive) {
      setConfirmationCooldownNow(Date.now());
    }
  }, [confirmationCooldownActive]);

  useEffect(() => {
    const lastSentAt = getDateFromValue(appointment?.checkInLinkSentAt);
    if (!lastSentAt) {
      setCheckInLinkCooldownUntil(null);
      return;
    }
    setCheckInLinkCooldownUntil(lastSentAt.getTime() + CHECK_IN_LINK_COOLDOWN_MS);
  }, [appointment?.id, appointment?.checkInLinkSentAt]);

  useEffect(() => {
    if (!checkInLinkCooldownActive) return undefined;
    const intervalId = window.setInterval(() => {
      setCheckInLinkCooldownNow(Date.now());
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [checkInLinkCooldownActive]);

  useEffect(() => {
    if (!checkInLinkCooldownActive) {
      setCheckInLinkCooldownNow(Date.now());
    }
  }, [checkInLinkCooldownActive]);

  const getColor = (type) => ({
    confirm: '#4CAF50',
    cancel: '#F44336',
    noShow: '#9C27B0',
    reschedule: '#2196F3',
    sendReminder: '#03A9F4',
    sendConfirmationRequest: '#0f766e',
    sendCheckInLink: '#00695C',
  }[type] || '#9e9e9e');

  return (
    <div className="quick-actions quick-actions--plain" onClick={(e) => e.stopPropagation()}>
      <div className="quick-actions__buttons">
        {!isConfirmedAppointment && (
          <button type="button" className="quick-actions__btn" title="Confirm" disabled={isDisabled('confirm')} onClick={(e) => openModal(e, 'confirm')} style={{ '--qa-color': getColor('confirm') }}>
            <CheckIcon />
            <span className="quick-actions__label">Confirm</span>
          </button>
        )}
        <button type="button" className="quick-actions__btn" title="Cancel" disabled={isDisabled('cancel')} onClick={(e) => openModal(e, 'cancel')} style={{ '--qa-color': getColor('cancel') }}>
          <XIcon />
          <span className="quick-actions__label">Cancel</span>
        </button>
        <button type="button" className="quick-actions__btn" title="Reschedule" disabled={isDisabled('reschedule')} onClick={() => setRescheduleOpen(true)} style={{ '--qa-color': getColor('reschedule') }}>
          <CalendarIcon />
          <span className="quick-actions__label">Reschedule</span>
        </button>
        {awaitingClientConfirmation && (
          <button
            type="button"
            className="quick-actions__btn"
            title={
              confirmationCooldownActive
                ? (isPendingConfirmationReplyOnlyEnabled()
                    ? `Confirmation SMS available again in ${confirmationCooldownLabel}`
                    : `Confirmation link available again in ${confirmationCooldownLabel}`)
                : (hasReminderPhone
                    ? (isPendingConfirmationReplyOnlyEnabled() ? 'Send Confirmation SMS' : 'Send Confirmation Link')
                    : 'No phone')
            }
            disabled={isDisabled('sendConfirmationRequest')}
            onClick={(e) => openModal(e, 'sendConfirmationRequest')}
            style={{ '--qa-color': getColor('sendConfirmationRequest') }}
          >
            {sendingConfirmationRequest ? <span className="quick-actions__spinner" /> : <LinkIcon />}
            <span className="quick-actions__label">
              {confirmationCooldownActive
                ? (isPendingConfirmationReplyOnlyEnabled()
                    ? `Confirm SMS (${confirmationCooldownLabel})`
                    : `Confirm Link (${confirmationCooldownLabel})`)
                : (isPendingConfirmationReplyOnlyEnabled() ? 'Confirm SMS' : 'Confirm Link')}
            </span>
          </button>
        )}
        {checkInLinkState.eligible && !appointmentIsCheckedIn && bookingSettings.allowClientCheckIn === true && (
          <button
            type="button"
            className="quick-actions__btn"
            title={
              checkInLinkCooldownActive
                ? `Check-in link available again in ${checkInLinkCooldownLabel}`
                : (hasReminderPhone ? 'Send self check-in link' : 'No phone')
            }
            disabled={isDisabled('sendCheckInLink')}
            onClick={(e) => openModal(e, 'sendCheckInLink')}
            style={{ '--qa-color': getColor('sendCheckInLink') }}
          >
            {sendingCheckInLink ? <span className="quick-actions__spinner" /> : <LinkIcon />}
            <span className="quick-actions__label">
              {checkInLinkCooldownActive ? `Check-In Link (${checkInLinkCooldownLabel})` : 'Check-In Link'}
            </span>
          </button>
        )}
        {awaitingClientConfirmation && (
          <button
            type="button"
            className="quick-actions__btn"
            title={
              reminderCooldownActive
                ? `Reminder available again in ${reminderCooldownLabel}`
                : (hasReminderEmail || hasReminderPhone ? 'Send Reminder' : 'No email or phone')
            }
            disabled={isDisabled('sendReminder')}
            onClick={(e) => openModal(e, 'sendReminder')}
            style={{ '--qa-color': getColor('sendReminder') }}
          >
            {sendingReminder ? <span className="quick-actions__spinner" /> : <BellIcon />}
            <span className="quick-actions__label">
              {reminderCooldownActive ? `Reminder (${reminderCooldownLabel})` : 'Reminder'}
            </span>
          </button>
        )}
        <button type="button" className="quick-actions__btn" title="No Show" disabled={isDisabled('noShow')} onClick={(e) => openModal(e, 'noShow')} style={{ '--qa-color': getColor('noShow') }}>
          <PersonOffIcon />
          <span className="quick-actions__label">No Show</span>
        </button>
      </div>

      {modal.open && (
        <div className="quick-actions__overlay" onClick={closeModal} role="dialog" aria-modal="true">
          <div className="quick-actions__modal" onClick={(e) => e.stopPropagation()}>
            {modal.title && <h3 className="quick-actions__modal-title">{modal.title}</h3>}
            <p className="quick-actions__modal-message">{modal.message}</p>
            {modal.type === 'cancel' && (
              <div className="quick-actions-cancel-reason-wrap">
                <label htmlFor="quick-actions-cancel-reason" className="quick-actions-cancel-reason-label">
                  Cancellation reason <span aria-hidden="true">*</span>
                </label>
                <textarea
                  id="quick-actions-cancel-reason"
                  rows={3}
                  value={cancelReason}
                  onChange={(e) => { setCancelReason(e.target.value); setCancelReasonError(''); }}
                  placeholder="Enter reason..."
                  className={`quick-actions-cancel-reason-input${cancelReasonError ? ' has-error' : ''}`}
                />
                {cancelReasonError && <p className="quick-actions-cancel-reason-help is-error">{cancelReasonError}</p>}
              </div>
            )}
            {modal.type === 'sendReminder' && (
              <fieldset className="quick-actions-reminder-options">
                <label className={`quick-actions-reminder-options__option${!hasReminderEmail ? ' is-disabled' : ''}`}>
                  <input
                    type="radio"
                    name="quick-actions-reminder-channel"
                    value="email"
                    checked={reminderChannel === 'email'}
                    onChange={(e) => setReminderChannel(e.target.value)}
                    disabled={!hasReminderEmail}
                  />
                  <span>Email</span>
                </label>
                <label className={`quick-actions-reminder-options__option${!hasReminderPhone ? ' is-disabled' : ''}`}>
                  <input
                    type="radio"
                    name="quick-actions-reminder-channel"
                    value="sms"
                    checked={reminderChannel === 'sms'}
                    onChange={(e) => setReminderChannel(e.target.value)}
                    disabled={!hasReminderPhone}
                  />
                  <span>SMS</span>
                </label>
                <label className={`quick-actions-reminder-options__option${!(hasReminderEmail && hasReminderPhone) ? ' is-disabled' : ''}`}>
                  <input
                    type="radio"
                    name="quick-actions-reminder-channel"
                    value="both"
                    checked={reminderChannel === 'both'}
                    onChange={(e) => setReminderChannel(e.target.value)}
                    disabled={!(hasReminderEmail && hasReminderPhone)}
                  />
                  <span>Email and SMS</span>
                </label>
                <p className="quick-actions-reminder-options__help">
                  {!hasReminderEmail && !hasReminderPhone
                    ? 'This client does not have an email address or phone number saved.'
                    : !hasReminderEmail
                      ? 'Email is unavailable because this client does not have an email address saved.'
                      : !hasReminderPhone
                        ? 'SMS is unavailable because this client does not have a phone number saved.'
                        : 'Choose one delivery method or send both at once.'}
                </p>
              </fieldset>
            )}
            <div className="quick-actions__modal-actions">
              <button type="button" className="quick-actions-dialog-btn quick-actions-dialog-btn--secondary" onClick={closeModal} disabled={submitting}>Cancel</button>
              <button type="button" className="quick-actions-dialog-btn quick-actions-dialog-btn--primary" onClick={doConfirm} disabled={submitting || (modal.type === 'cancel' && !cancelReason.trim())}>
                {submitting ? 'Working…' : (modal.type === 'sendReminder' || modal.type === 'sendConfirmationRequest' || modal.type === 'sendCheckInLink' ? 'Send' : 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      <RescheduleDialog
        open={rescheduleOpen}
        onClose={handleRescheduleClose}
        appointment={appointment}
        onReschedule={handleRescheduleConfirm}
        onError={(message) => showToast(message || 'Reschedule failed', 'error')}
      />
    </div>
  );
};

export default QuickActions;
